import React, { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  FileCheck2,
  Gem,
  Instagram,
  LockKeyhole,
  RefreshCw,
  Send,
  ShieldCheck,
  Sparkles,
  Upload,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import Button from '../common/design-system/Button';
import ImageWithFallback from '../common/ImageWithFallback';
import RichTextDisplay, { TermsDisplay } from '../common/RichTextDisplay';
import PaidCollabPaymentStatus from '../payments/PaidCollabPaymentStatus';
import CreatorSubmissionPanel from './CreatorSubmissionPanel';
import CreatorPerformanceCard from './CreatorPerformanceCard';
import baseStyles from '../business/BusinessCollabJourney.module.scss';
import styles from './CreatorCollabJourney.module.scss';

const STEPS = [
  { id: 'opportunity', number: '01', icon: Sparkles },
  { id: 'application', number: '02', icon: Send },
  { id: 'accepted', number: '03', icon: CheckCircle2 },
  { id: 'deliverables', number: '04', icon: Upload },
  { id: 'review', number: '05', icon: FileCheck2 },
  { id: 'earnings', number: '06', icon: Gem },
  { id: 'impact', number: '07', icon: ShieldCheck },
];

const formatDate = (value, language) => value
  ? new Intl.DateTimeFormat(language?.startsWith('pt') ? 'pt-BR' : language?.startsWith('en') ? 'en-US' : 'es-PA', { day: '2-digit', month: 'short', year: 'numeric' }).format(new Date(value))
  : '—';

const formatMoney = (value, currency = 'USD', language) => new Intl.NumberFormat(
  language?.startsWith('pt') ? 'pt-BR' : language?.startsWith('en') ? 'en-US' : 'es-PA',
  { style: 'currency', currency, currencyDisplay: 'code', minimumFractionDigits: 2 },
).format(Number(value || 0));

const isPaidOffer = offer => ['paid', 'hybrid'].includes(offer?.compensation_mode) || Boolean(offer?.sponsor_this_offer);

const getInitialStep = ({ paid, isAccepted, isRejected, hasApplied, isDeliverablesCompleted, submittedCount, applicationStatus }) => {
  if (applicationStatus === 'completed') return paid ? 5 : 6;
  if (isAccepted && isDeliverablesCompleted) return 4;
  if (isAccepted && submittedCount > 0) return 3;
  if (isAccepted) return 2;
  if (isRejected || hasApplied) return 1;
  return 0;
};

const getUnlockedStep = ({ isAccepted, isRejected, hasApplied, isDeliverablesReady, submittedCount, applicationStatus }) => {
  if (applicationStatus === 'completed') return 6;
  if (isAccepted && isDeliverablesReady) return 4;
  if (isAccepted && submittedCount > 0) return 3;
  if (isAccepted) return 2;
  if (isRejected || hasApplied) return 1;
  return 0;
};

const StepHeader = ({ step, title, description, number }) => (
  <header className={baseStyles.stepHeader}>
    <div>
      <div className={baseStyles.eyebrow}>{number} · {step}</div>
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
    <div className={baseStyles.stepNumber}>{number}</div>
  </header>
);

const StatusPill = ({ children, tone = 'neutral' }) => (
  <span className={`${baseStyles.statusPill} ${baseStyles[`tone-${tone}`]}`}>{children}</span>
);

const CreatorCollabJourney = ({
  id,
  offer,
  phase,
  isInvitation = false,
  isFull = false,
  hasApplied = false,
  isAccepted = false,
  isRejected = false,
  applicationStatus,
  isDeliverablesReady,
  isInstagramConnected = false,
  submittedCount = 0,
  totalDeliverables = 0,
  submittedDeliverables = [],
  deliverablesMode = 'defined',
  isDeliverablesCompleted = false,
  performance = null,
  performanceLoading = true,
  creatorWallet = null,
  requiredDeliverables = [],
  deliverablesCounts = {},
  onApply,
  onAcceptInvitation,
  onDeclineInvitation,
  onCreatorConfirmCompletion,
  onInstagramConnect,
  onDeliverablesProgress,
  onPerformanceUpdate,
  onBack,
  onPayoutSetup,
  onOpenReport,
  onRefresh,
  applying = false,
  confirmingCompletion = false,
}) => {
  const { t, i18n } = useTranslation('collab');
  const language = i18n.resolvedLanguage || i18n.language || 'es';
  const isOpenMode = deliverablesMode === 'open';
  const paid = isPaidOffer(offer);
  const initialStep = getInitialStep({ paid, isAccepted, isRejected, hasApplied, isDeliverablesCompleted, submittedCount, applicationStatus });
  const deliverablesReady = isDeliverablesReady ?? isDeliverablesCompleted;
  const unlockedStep = getUnlockedStep({ isAccepted, isRejected, hasApplied, isDeliverablesReady: deliverablesReady, submittedCount, applicationStatus });
  const [activeStep, setActiveStep] = useState(initialStep);
  const [opportunityImageIndex, setOpportunityImageIndex] = useState(0);

  useEffect(() => setActiveStep(initialStep), [id, initialStep]);

  const featuredImage = offer?.featured_image_url || offer?.featured_image;
  const additionalImages = useMemo(() => {
    if (!Array.isArray(offer?.additional_images)) return [];
    return offer.additional_images
      .map(img => (typeof img === 'string' ? img : (img?.url || img?.image || img?.file)))
      .filter(Boolean);
  }, [offer?.additional_images]);

  const allOpportunityImages = useMemo(() => {
    const list = [];
    if (featuredImage) list.push(featuredImage);
    additionalImages.forEach(img => {
      if (!list.includes(img)) list.push(img);
    });
    return list;
  }, [featuredImage, additionalImages]);

  const currentPosterImage = allOpportunityImages[opportunityImageIndex] || featuredImage;

  const handlePrevOpportunityImage = () => {
    setOpportunityImageIndex(prev => (prev === 0 ? allOpportunityImages.length - 1 : prev - 1));
  };
  const handleNextOpportunityImage = () => {
    setOpportunityImageIndex(prev => (prev === allOpportunityImages.length - 1 ? 0 : prev + 1));
  };

  const creatorValue = Number(offer?.total_creator_value ?? (Number(offer?.exchange_value || offer?.value || 0) + Number(offer?.cash_compensation || 0)));
  const cashValue = Number(offer?.cash_compensation || (offer?.sponsor_this_offer ? offer?.sponsor_amount : 0) || 0);
  const exchangeValue = Number(offer?.exchange_value ?? offer?.value ?? 0);
  const currency = offer?.cash_currency || 'USD';
  const appApproved = applicationStatus === 'completed';
  const stepLabels = useMemo(() => ({
    opportunity: t('creatorJourney.steps.opportunity'),
    application: t('creatorJourney.steps.application'),
    accepted: t('creatorJourney.steps.accepted'),
    deliverables: t('creatorJourney.steps.deliverables'),
    review: t('creatorJourney.steps.review'),
    earnings: t('creatorJourney.steps.earnings'),
    impact: t('creatorJourney.steps.impact'),
  }), [t]);

  const goToStep = step => {
    if (step <= unlockedStep) setActiveStep(step);
  };

  const openDeliverables = () => {
    setActiveStep(3);
    if (!isInstagramConnected) onInstagramConnect?.();
  };

  const renderOpportunityActions = () => {
    if (activeStep !== 0 || isFull) return null;
    if (isInvitation) {
      return (
        <div className={styles.asideActions}>
          <Button
            variant="primary"
            onClick={onAcceptInvitation}
            disabled={applying}
            className={styles.asidePrimaryButton}
          >
            {t('creatorJourney.actions.accept')} <ArrowRight size={17} />
          </Button>
          <Button
            variant="outline"
            onClick={onDeclineInvitation}
            disabled={applying}
            className={styles.asideSecondaryButton}
          >
            {t('creatorJourney.actions.decline')}
          </Button>
        </div>
      );
    }
    if (!hasApplied && !isRejected && !isFull) {
      return (
        <div className={styles.asideActions}>
          <Button
            variant="primary"
            onClick={onApply}
            disabled={applying}
            aria-busy={applying}
            className={styles.asidePrimaryButton}
          >
            {t('creatorJourney.actions.apply')} <ArrowRight size={17} />
          </Button>
        </div>
      );
    }
    return null;
  };

  const renderMobileStickyBar = () => {
    if (activeStep !== 0 || isFull) return null;
    if (!isInvitation && (hasApplied || isRejected)) return null;

    return (
      <aside
        className={styles.mobileStickyBar}
        aria-label={t('creatorJourney.opportunity.actions_aria', { defaultValue: 'Acciones de oportunidad' })}
      >
        <div className={styles.mobileStickySummary}>
          <span className={styles.mobileStickyLabel}>{t('creatorJourney.opportunity.total_value')}</span>
          <strong className={styles.mobileStickyValue}>{formatMoney(creatorValue, currency, language)}</strong>
        </div>
        <div className={styles.mobileStickyActions}>
          {isInvitation ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={onDeclineInvitation}
                disabled={applying}
                className={styles.mobileDeclineButton}
              >
                {t('creatorJourney.actions.decline')}
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={onAcceptInvitation}
                disabled={applying}
                className={styles.mobileAcceptButton}
              >
                {t('creatorJourney.actions.accept')} <ArrowRight size={16} />
              </Button>
            </>
          ) : (
            <Button
              variant="primary"
              size="sm"
              onClick={onApply}
              disabled={applying}
              aria-busy={applying}
              className={styles.mobileApplyButton}
            >
              {t('creatorJourney.actions.apply')} <ArrowRight size={16} />
            </Button>
          )}
        </div>
      </aside>
    );
  };

  const renderOpportunity = () => (
    <>
      <StepHeader step={stepLabels.opportunity} number="01" title={t('creatorJourney.opportunity.title')} description={t('creatorJourney.opportunity.description')} />
      <div className={`${baseStyles.frame} ${styles.opportunityFrame}`}>
        <div className={`${baseStyles.frameGrid} ${styles.opportunityGrid}`}>
          <main className={baseStyles.frameMain}>
            <div className={styles.opportunityHero}>
              <div className={styles.opportunityCopy}>
                <div className={baseStyles.statusRow}>
                  <StatusPill tone={isFull ? 'warning' : 'success'}>{isFull && <LockKeyhole size={13} />} {isFull ? t('creatorJourney.opportunity.full') : t('creatorJourney.opportunity.active')}</StatusPill>
                  <StatusPill>{paid ? t('creatorJourney.opportunity.paid') : t('creatorJourney.opportunity.exchange')}</StatusPill>
                  {isInvitation && <StatusPill tone="warning">{t('creatorJourney.opportunity.invited')}</StatusPill>}
                </div>
                <div className={styles.businessPill}>{offer?.business_name || t('creatorJourney.common.business')}</div>
                <h2>{offer?.title || t('creatorJourney.common.untitled')}</h2>
                <p>{t('creatorJourney.opportunity.intro')}</p>
                {isFull && <div className={styles.fullNotice} role="status"><LockKeyhole size={18} /><div><strong>{t('creatorJourney.opportunity.full')}</strong><span>{t('view.collab_full')}</span></div></div>}
              </div>
              <div className={baseStyles.posterWrapper}>
                <div className={`${baseStyles.poster} ${styles.opportunityPoster}`} aria-label={t('creatorJourney.common.visual_aria')}>
                  <ImageWithFallback className={styles.posterImage || baseStyles.posterImage} src={currentPosterImage} alt={offer?.title} name={offer?.title} />
                  {!currentPosterImage && <strong>{offer?.business_name || 'FOTH'}</strong>}
                  {allOpportunityImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        className={`${baseStyles.posterNavButton} ${baseStyles.posterNavPrev}`}
                        onClick={handlePrevOpportunityImage}
                        aria-label={t('view.prev_image', { defaultValue: 'Imagen anterior' })}
                      >
                        <ChevronLeft size={18} />
                      </button>
                      <button
                        type="button"
                        className={`${baseStyles.posterNavButton} ${baseStyles.posterNavNext}`}
                        onClick={handleNextOpportunityImage}
                        aria-label={t('view.next_image', { defaultValue: 'Siguiente imagen' })}
                      >
                        <ChevronRight size={18} />
                      </button>
                      <span className={baseStyles.posterBadge}>
                        {opportunityImageIndex + 1} / {allOpportunityImages.length}
                      </span>
                    </>
                  )}
                </div>
                {allOpportunityImages.length > 1 && (
                  <div className={baseStyles.posterThumbnails} role="group" aria-label={t('view.thumbnails_aria', { defaultValue: 'Miniaturas de imágenes' })}>
                    {allOpportunityImages.map((img, idx) => (
                      <button
                        key={idx}
                        type="button"
                        className={`${baseStyles.posterThumb} ${idx === opportunityImageIndex ? baseStyles.activeThumb : ''}`}
                        onClick={() => setOpportunityImageIndex(idx)}
                        aria-label={t('view.select_image', { index: idx + 1, defaultValue: `Seleccionar imagen ${idx + 1}` })}
                      >
                        <img src={img} alt="" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className={`${baseStyles.metricStrip} ${styles.opportunityMetrics}`}>
              <div className={baseStyles.compactMetric}><span>{t('creatorJourney.opportunity.creator_earnings')}</span><strong>{formatMoney(cashValue, currency, language)}</strong></div>
              <div className={baseStyles.compactMetric}><span>{t('creatorJourney.opportunity.exchange_value')}</span><strong>{formatMoney(exchangeValue, currency, language)}</strong></div>
              <div className={baseStyles.compactMetric}><span>{t('creatorJourney.opportunity.deliverables')}</span><strong>{isOpenMode ? t('creatorJourney.opportunity.open') : totalDeliverables || '—'}</strong></div>
            </div>
            <div className={`${baseStyles.contentCard} ${baseStyles.firstContentCard}`}>
              <div className={baseStyles.sectionLabel}>{t('creatorJourney.opportunity.summary_label')}</div>
              <RichTextDisplay content={offer?.description} />
            </div>
            {offer?.terms_and_conditions && <div className={baseStyles.contentCard}>
              <div className={baseStyles.sectionLabel}>{t('creatorJourney.opportunity.terms_label')}</div>
              <TermsDisplay content={offer.terms_and_conditions} />
            </div>}
            {additionalImages.length > 0 && (
              <div className={baseStyles.contentCard}>
                <div className={baseStyles.sectionLabel}>{t('view.gallery_label', { defaultValue: 'Galería de imágenes' })}</div>
                <div className={baseStyles.galleryGrid}>
                  {allOpportunityImages.map((img, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`${baseStyles.galleryCardItem} ${idx === opportunityImageIndex ? baseStyles.galleryCardItemActive : ''}`}
                      onClick={() => setOpportunityImageIndex(idx)}
                      aria-label={t('view.select_image', { index: idx + 1, defaultValue: `Seleccionar imagen ${idx + 1}` })}
                    >
                      <img src={img} alt={`Imagen ${idx + 1}`} />
                      {idx === 0 && <span className={baseStyles.galleryTag}>{t('view.featured_tag', { defaultValue: 'Portada' })}</span>}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </main>
          <aside className={`${baseStyles.frameAside} ${styles.opportunityAside}`}>
            <div className={baseStyles.sectionLabel}>{t('creatorJourney.opportunity.details_label')}</div>
            <div className={baseStyles.facts}>
              <div className={baseStyles.fact}><span>{t('creatorJourney.opportunity.location')}</span><strong>{offer?.country_name || t('creatorJourney.common.global')}</strong></div>
              <div className={baseStyles.fact}><span>{t('creatorJourney.opportunity.dates')}</span><strong>{formatDate(offer?.from_date, language)} – {formatDate(offer?.to_date, language)}</strong></div>
              <div className={baseStyles.fact}><span>{t('creatorJourney.opportunity.applicants')}</span><strong>{offer?.applications_count || 0}</strong></div>
              <div className={baseStyles.fact}><span>{t('creatorJourney.opportunity.format')}</span><strong>{offer?.compensation_type || t('creatorJourney.common.collab')}</strong></div>
            </div>
            <div className={baseStyles.moneyCard}><span>{t('creatorJourney.opportunity.total_value')}</span><strong>{formatMoney(creatorValue, currency, language)}</strong><small>{t('creatorJourney.opportunity.total_value_help')}</small></div>
            {renderOpportunityActions()}
          </aside>
        </div>
        {renderMobileStickyBar()}
      </div>
    </>
  );

  const renderApplication = () => (
    <>
      <StepHeader step={stepLabels.application} number="02" title={isRejected ? t('creatorJourney.application.rejected_title') : t('creatorJourney.application.title')} description={isRejected ? t('creatorJourney.application.rejected_body') : t('creatorJourney.application.description')} />
      <div className={baseStyles.frame}>
        <div className={baseStyles.frameGrid}>
          <main className={baseStyles.frameMain}>
            <div className={styles.applicationHero}>
              <div className={baseStyles.statusRow}>
                <StatusPill tone={isRejected ? 'danger' : 'warning'}>{isRejected ? t('creatorJourney.application.rejected') : t('creatorJourney.application.sent')}</StatusPill>
                <StatusPill>{isRejected ? t('creatorJourney.application.rejected_status') : t('creatorJourney.application.in_review')}</StatusPill>
              </div>
              <h2>{isRejected ? t('creatorJourney.application.rejected_status') : t('creatorJourney.application.heading')}</h2>
              <p>{isRejected ? t('creatorJourney.application.rejected_body') : t('creatorJourney.application.sent_help')}</p>
              {!isRejected && (
                <ol className={styles.applicationTimeline} aria-label={t('creatorJourney.application.process_label')}>
                  <li className={styles.applicationStep} data-state="complete">
                    <span className={styles.applicationStepMarker} aria-hidden="true"><Check size={14} /></span>
                    <div className={styles.applicationStepCopy}>
                      <strong>{t('creatorJourney.application.received')}</strong>
                      <p>{t('creatorJourney.application.received_body')}</p>
                    </div>
                  </li>
                  <li className={styles.applicationStep} data-state="current" aria-current="step">
                    <span className={styles.applicationStepMarker} aria-hidden="true">2</span>
                    <div className={styles.applicationStepCopy}>
                      <strong>{t('creatorJourney.application.business_review')}</strong>
                      <p>{t('creatorJourney.application.review_body')}</p>
                    </div>
                  </li>
                  <li className={styles.applicationStep} data-state="pending">
                    <span className={styles.applicationStepMarker} aria-hidden="true">3</span>
                    <div className={styles.applicationStepCopy}>
                      <strong>{t('creatorJourney.application.selection')}</strong>
                      <p>{t('creatorJourney.application.selection_body')}</p>
                    </div>
                  </li>
                </ol>
              )}
              {!isRejected && (
                <div className={styles.applicationReviewNotice}>
                  <span className={styles.applicationNoticeMark} aria-hidden="true">{(offer?.business_name || 'F').slice(0, 1).toUpperCase()}</span>
                  <div className={styles.applicationNoticeCopy}>
                    <strong>{offer?.business_name || t('creatorJourney.common.business')} {t('creatorJourney.application.profile_review')}</strong>
                    <span>{t('creatorJourney.application.in_review')}</span>
                  </div>
                  <div className={styles.applicationNoticeStatus}><StatusPill tone="warning">{t('creatorJourney.application.pending')}</StatusPill></div>
                </div>
              )}
            </div>
          </main>
          <aside className={baseStyles.frameAside}>
            <div className={baseStyles.sectionLabel}>{t('creatorJourney.application.participation_label')}</div>
            <div className={baseStyles.facts}>
              <div className={baseStyles.fact}><span>{t('creatorJourney.application.status_label')}</span><strong>{isRejected ? t('creatorJourney.application.rejected_status') : t('creatorJourney.application.sent')}</strong></div>
              <div className={baseStyles.fact}><span>{t('creatorJourney.application.creator')}</span><strong>{t('creatorJourney.application.you')}</strong></div>
              <div className={baseStyles.fact}><span>{t('creatorJourney.application.instagram')}</span><strong>{isInstagramConnected ? t('creatorJourney.application.connected') : t('creatorJourney.application.not_connected')}</strong></div>
              <div className={baseStyles.fact}><span>{t('creatorJourney.accepted.creator_value')}</span><strong>{formatMoney(creatorValue, currency, language)}</strong></div>
            </div>
            <div className={`${baseStyles.contentCard} ${styles.lockedCard}`}>
              <h3>{t('creatorJourney.application.locked_title')}</h3>
              <p>{t('creatorJourney.application.locked_body')}</p>
            </div>
          </aside>
        </div>
      </div>
    </>
  );

  const renderAccepted = () => (
    <>
      <StepHeader step={stepLabels.accepted} number="03" title={t('creatorJourney.accepted.title')} description={t('creatorJourney.accepted.description')} />
      <div className={`${baseStyles.frame} ${styles.acceptedFrame}`}>
        <div className={baseStyles.frameMain}>
          <div className={baseStyles.statusRow}><StatusPill tone="success"><CheckCircle2 size={13} /> {t('creatorJourney.accepted.confirmed')}</StatusPill><StatusPill><Instagram size={13} /> {isInstagramConnected ? t('creatorJourney.accepted.instagram_connected') : t('creatorJourney.accepted.instagram_needed')}</StatusPill></div>
          <div className={baseStyles.sectionLabel}>{t('creatorJourney.accepted.next_label')}</div>
          <h2 className={baseStyles.fundingTitle}>{t('creatorJourney.accepted.heading')}</h2>
          <p className={baseStyles.panelCopy}>{t('creatorJourney.accepted.body')}</p>
          <div className={baseStyles.metricGrid}>
            <div className={baseStyles.metric}><span>{t('creatorJourney.accepted.deliverables')}</span><strong>{isOpenMode ? t('creatorJourney.accepted.open') : totalDeliverables || '—'}</strong></div>
            <div className={baseStyles.metric}><span>{t('creatorJourney.accepted.deadline')}</span><strong>{formatDate(offer?.to_date, language)}</strong></div>
            <div className={baseStyles.metric}><span>{t('creatorJourney.accepted.creator_value')}</span><strong>{formatMoney(creatorValue, currency, language)}</strong></div>
          </div>
        </div>
      </div>
    </>
  );

  const renderDeliverables = () => (
    <>
      <StepHeader step={stepLabels.deliverables} number="04" title={t('creatorJourney.deliverables.title')} description={t('creatorJourney.deliverables.description')} />
    </>
  );

  const renderReview = () => (
    <>
      <StepHeader step={stepLabels.review} number="05" title={t('creatorJourney.review.title')} description={t('creatorJourney.review.description')} />
      <div className={styles.reviewSummary}>
        <div className={styles.reviewSummaryCopy}>
          <div className={styles.reviewStatusRow}>
            <StatusPill tone="success">{t('creatorJourney.review.deliverables_count', { submitted: submittedCount, total: totalDeliverables || submittedCount })}</StatusPill>
            <StatusPill tone={appApproved ? 'success' : 'warning'}>{appApproved ? t('creatorJourney.review.approved_status') : t('creatorJourney.review.in_progress')}</StatusPill>
          </div>
          <h2>{t('creatorJourney.review.summary_title')}</h2>
          <p>{appApproved ? t('creatorJourney.review.approved_help') : t('creatorJourney.review.pending_help')}</p>
        </div>
        <div className={styles.reviewStamp}>
          <strong>{submittedCount}/{totalDeliverables || submittedCount}</strong>
          <span>{t('creatorJourney.review.deliverables_stamp')}</span>
        </div>
      </div>
      <div className={styles.reviewGrid}>
        <section className={styles.reviewList}>
          <header className={styles.reviewPanelHead}>
            <div className={baseStyles.sectionLabel}>{t('creatorJourney.review.submitted_label')}</div>
            <h3>{t('creatorJourney.review.registered_title')}</h3>
            <p>{t('creatorJourney.review.registered_body')}</p>
          </header>
          <div className={styles.reviewItems}>
            {submittedDeliverables.length > 0 ? submittedDeliverables.map(item => (
              <div className={styles.reviewItem} key={item.id}>
                <span className={styles.reviewItemIcon}><Instagram size={17} aria-hidden="true" /></span>
                <div className={styles.reviewItemIdentity}>
                  <strong>{item.label} {item.total > 1 ? `(${item.index}/${item.total})` : ''}</strong>
                  <span>{t('creatorJourney.review.metrics_connected')}</span>
                </div>
                <div className={styles.reviewItemStatus}><StatusPill tone="success">{appApproved ? t('creatorJourney.review.approved') : t('creatorJourney.review.registered')}</StatusPill></div>
              </div>
            )) : (
              <div className={styles.reviewItem}>
                <span className={styles.reviewItemIcon}><Check size={16} aria-hidden="true" /></span>
                <div className={styles.reviewItemIdentity}>
                  <strong>{t('creatorJourney.review.deliverables_count', { submitted: submittedCount, total: totalDeliverables || submittedCount })}</strong>
                  <span>{t('creatorJourney.review.metrics_connected')}</span>
                </div>
                <div className={styles.reviewItemStatus}><StatusPill tone="success">{t('creatorJourney.review.registered')}</StatusPill></div>
              </div>
            )}
          </div>
        </section>
        <section className={`${baseStyles.panel} ${styles.performancePanel}`}>
          <div className={baseStyles.sectionLabel}>{t('creatorJourney.review.performance_label')}</div>
          <h2>{t('creatorJourney.review.performance_title')}</h2>
          <p>{t('creatorJourney.review.performance_body')}</p>
          <div className={styles.performanceCompact}><CreatorPerformanceCard performance={performance} loading={performanceLoading} /></div>
        </section>
      </div>
      {!appApproved && !paid && deliverablesReady && !offer?.delivery_confirmed && (
        <div className={baseStyles.statusBanner} role="status">
          <div><CheckCircle2 size={21} /><div><strong>{t('creatorJourney.review.creator_confirm_title')}</strong><span>{t('creatorJourney.review.creator_confirm_help')}</span></div></div>
          <Button variant="primary" onClick={onCreatorConfirmCompletion} disabled={confirmingCompletion} loading={confirmingCompletion}>{t('creatorJourney.review.creator_confirm_action')}</Button>
        </div>
      )}
      {!appApproved && paid && deliverablesReady && !offer?.delivery_confirmed && !['under_business_review', 'manually_approved', 'automatically_approved', 'issue_reported'].includes(offer?.approval_status) && (
        <div className={baseStyles.statusBanner} role="status">
          <div><CheckCircle2 size={21} /><div><strong>{t('creatorJourney.review.paid_creator_confirm_title', 'Entregas completas — Confirmar entrega')}</strong><span>{t('creatorJourney.review.paid_creator_confirm_help', 'Has registrado todos tus entregables requeridos. Al confirmar, notificarás al negocio y comenzará su plazo de 3 días hábiles para revisar tu contenido.')}</span></div></div>
          <Button variant="primary" onClick={onCreatorConfirmCompletion} disabled={confirmingCompletion} loading={confirmingCompletion}>{t('creatorJourney.review.paid_creator_confirm_action', 'Confirmar entrega de contenidos')}</Button>
        </div>
      )}
      {!appApproved && paid && (offer?.approval_status === 'under_business_review' || offer?.delivery_confirmed) && (
        <div className={baseStyles.statusBanner} role="status">
          <div><CalendarClock size={21} /><div><strong>{t('creatorJourney.review.under_review_warning_title', 'Tus entregas están en revisión por el negocio')}</strong><span>{t('creatorJourney.review.under_review_warning_body', { deadline: offer?.business_review_deadline ? formatDate(offer.business_review_deadline, language) : '' }, 'El negocio tiene hasta 3 días hábiles para revisar tus entregas o reportar un problema. Si no responden dentro de ese plazo, tu entrega se aprobará automáticamente.')}</span></div></div>
        </div>
      )}
    </>
  );

  const renderEarnings = () => {
    const isApproved = appApproved;
    const hasCash = cashValue > 0;
    const hasExchange = exchangeValue > 0;

    return (
      <>
        <StepHeader
          step={stepLabels.earnings}
          number="06"
          title={isApproved ? t('creatorJourney.earnings.title') : t('creatorJourney.earnings.pending_title')}
          description={isApproved ? t('creatorJourney.earnings.description') : t('creatorJourney.earnings.pending_body')}
        />
        <div className={baseStyles.paymentFlowFrame}>
          <div className={styles.earningsSummaryGrid}>
            {hasCash && (
              <div className={`${styles.earningsCard} ${styles.earningsCardPrimary}`}>
                <div className={styles.earningsCardHeader}>
                  <span>{t('creatorJourney.earnings.payment_amount')}</span>
                  <CircleDollarSign size={16} />
                </div>
                <strong className={styles.earningsAmount}>{formatMoney(cashValue, currency, language)}</strong>
                <small className={styles.earningsSubtext}>{t('creatorJourney.earnings.agreement')}</small>
              </div>
            )}

            <div className={styles.earningsCard}>
              <div className={styles.earningsCardHeader}>
                <span>{t('creatorJourney.earnings.status_label')}</span>
                {isApproved ? <ShieldCheck size={16} /> : <CalendarClock size={16} />}
              </div>
              <div
                className={styles.earningsStatusBadge}
                data-status={isApproved ? 'approved' : 'pending'}
              >
                {isApproved ? <CheckCircle2 size={15} /> : <CalendarClock size={15} />}
                <span>{isApproved ? t('creatorJourney.earnings.released') : t('creatorJourney.earnings.pending')}</span>
              </div>
              <small className={styles.earningsSubtext}>
                {isApproved ? t('creatorJourney.earnings.released_help') : t('creatorJourney.earnings.pending_help')}
              </small>
            </div>

            {hasExchange && (
              <div className={styles.earningsCard}>
                <div className={styles.earningsCardHeader}>
                  <span>{t('creatorJourney.earnings.exchange_value')}</span>
                  <Gem size={16} />
                </div>
                <strong className={styles.earningsAmount}>{formatMoney(exchangeValue, currency, language)}</strong>
                <small className={styles.earningsSubtext}>{t('creatorJourney.earnings.not_payout')}</small>
              </div>
            )}

            {!hasCash && !hasExchange && (
              <div className={styles.earningsCard}>
                <div className={styles.earningsCardHeader}>
                  <span>{t('creatorJourney.earnings.payment_amount')}</span>
                  <CircleDollarSign size={16} />
                </div>
                <strong className={styles.earningsAmount}>{formatMoney(0, currency, language)}</strong>
                <small className={styles.earningsSubtext}>{t('creatorJourney.earnings.agreement')}</small>
              </div>
            )}
          </div>
          <PaidCollabPaymentStatus collabId={id} role="creator" />
          <div className={baseStyles.paymentFlowNote}>
            <LockKeyhole size={18} />
            <div>
              <strong>{t('creatorJourney.earnings.important')}</strong>
              <p>{t('creatorJourney.earnings.important_body')}</p>
            </div>
          </div>
        </div>
        {creatorWallet && !creatorWallet.payout_setup_ready && (
          <div className={baseStyles.statusBanner}>
            <div>
              <ShieldCheck size={21} />
              <div>
                <strong>{t('creatorJourney.earnings.payout_title')}</strong>
                <span>{t('creatorJourney.earnings.payout_body')}</span>
              </div>
            </div>
            <Button variant="outline" onClick={onPayoutSetup}>
              {t('creatorJourney.earnings.payout_action')}
            </Button>
          </div>
        )}
      </>
    );
  };

  const renderImpact = () => (
    <>
      <StepHeader step={stepLabels.impact} number="07" title={t('creatorJourney.impact.title')} description={t('creatorJourney.impact.description')} />
      <div className={baseStyles.completionMasthead}><div className={baseStyles.completionMastheadCopy}><span className={baseStyles.completionEyebrow}><CheckCircle2 size={14} /> {t('creatorJourney.impact.badge')}</span><h2>{offer?.title}</h2><p>{t('creatorJourney.impact.body')}</p></div><div className={baseStyles.completionStamp}><strong>{isDeliverablesCompleted ? `${submittedCount}/${totalDeliverables || submittedCount}` : '—'}</strong><span>{t('creatorJourney.impact.deliverables_complete')}</span></div></div>
      <div className={styles.impactPerformance}><CreatorPerformanceCard performance={performance} loading={performanceLoading} /></div>
      <div className={baseStyles.emvCallout}><div><div className={baseStyles.sectionLabel}>{t('creatorJourney.impact.report_label')}</div><h2>{t('creatorJourney.impact.report_title')}</h2><p>{t('creatorJourney.impact.report_body')}</p></div><Button variant="outline" onClick={onOpenReport || (() => setActiveStep(6))}>{t('creatorJourney.impact.report_action')} <ArrowRight size={16} /></Button></div>
    </>
  );

  const scenes = [renderOpportunity, renderApplication, renderAccepted, renderDeliverables, renderReview, renderEarnings, renderImpact];
  const currentScene = scenes[activeStep] || scenes[0];
  const collabTitle = offer?.title || t('creatorJourney.common.untitled');

  return (
    <main className={`${baseStyles.journey} ${styles.creatorJourney} ${activeStep === 2 ? styles.darkJourney : ''}`} data-step={activeStep + 1}>
      <header className={`${baseStyles.topbar} ${styles.topbar}`}>
        <div className={baseStyles.topbarBrand}>
          <span className={baseStyles.brandMark}>FOTH</span>
          <span className={baseStyles.brandContext}>{t('creatorJourney.header.context')}</span>
          <div className={baseStyles.collabIdentity} title={collabTitle}>
            <span>{t('creatorJourney.common.collab')}</span>
            <strong>{collabTitle}</strong>
          </div>
        </div>
        <div className={baseStyles.topbarProgress}><span>{t('creatorJourney.header.step', { current: String(activeStep + 1).padStart(2, '0'), total: '07' })}</span><div className={baseStyles.progressTrack}><span style={{ width: `${((activeStep + 1) / STEPS.length) * 100}%` }} /></div><strong>{stepLabels[STEPS[activeStep]?.id]}</strong></div>
        <div className={baseStyles.topbarActions}><button className={baseStyles.refreshButton} type="button" onClick={onRefresh} aria-label={t('creatorJourney.header.refresh')}><RefreshCw size={17} /></button><button className={baseStyles.exitButton} type="button" onClick={onBack}><ArrowLeft size={16} /> {t('creatorJourney.header.back')}</button></div>
        <div className={baseStyles.mobileTitle}>
          <span title={collabTitle}>{collabTitle}</span>
          <strong>{STEPS[activeStep]?.number} · {stepLabels[STEPS[activeStep]?.id]}</strong>
        </div>
      </header>
      <div className={baseStyles.workspace}>
        <aside className={baseStyles.rail} aria-label={t('creatorJourney.rail.aria')}><div className={baseStyles.railEyebrow}>{t('creatorJourney.rail.eyebrow')}</div><ol>{STEPS.map((step, index) => { const Icon = step.icon; const done = index < activeStep && index < unlockedStep; const current = index === activeStep; const available = index <= unlockedStep; return <li key={step.id}><button type="button" className={`${baseStyles.railItem} ${done ? baseStyles.done : ''} ${current ? baseStyles.current : ''} ${available ? baseStyles.available : ''}`} onClick={() => goToStep(index)} disabled={!available} aria-current={current ? 'step' : undefined}><span className={baseStyles.railNumber}>{done ? <Check size={14} /> : <Icon size={14} />}</span><span><strong>{step.number} {stepLabels[step.id]}</strong><small>{done ? t('creatorJourney.rail.completed') : current ? t('creatorJourney.rail.in_progress') : available ? t('creatorJourney.rail.available') : t('creatorJourney.rail.next')}</small></span></button></li>; })}</ol><div className={baseStyles.railNote}><LockKeyhole size={14} /> {t('creatorJourney.rail.controlled')}</div></aside>
        <section className={baseStyles.stage}>
          <div className={baseStyles.scene}>
            {currentScene()}
            {isAccepted && (
              <div className={`${styles.submissionFrame} ${activeStep === 3 ? styles.submissionVisibleFrame : styles.submissionHiddenFrame}`}>
                <div className={activeStep === 3 ? `${baseStyles.frame} ${styles.deliverablesFrame}` : undefined}>
                  <div className={activeStep === 3 ? styles.deliverablesFrameMain : undefined}>
                    <CreatorSubmissionPanel offerId={id} isWinner={isAccepted} isInstagramConnected={isInstagramConnected} onInstagramConnect={onInstagramConnect} requiredDeliverables={requiredDeliverables} deliverablesCounts={deliverablesCounts} deliverablesMode={deliverablesMode} onDeliverablesProgress={onDeliverablesProgress} onPerformanceUpdate={onPerformanceUpdate} commandCenter />
                  </div>
                </div>
              </div>
            )}
            <div className={baseStyles.footerActions}>
              <Button variant="ghost" onClick={() => setActiveStep(Math.max(0, activeStep - 1))} disabled={activeStep === 0}>← {t('creatorJourney.actions.back')}</Button>
              <span className={baseStyles.microcopy}>{activeStep === 0 ? t('creatorJourney.footer.opportunity') : activeStep === 3 ? (deliverablesReady ? t('creatorJourney.footer.review_ready') : t('creatorJourney.footer.complete_deliverables')) : activeStep === 5 && !appApproved ? t('creatorJourney.footer.waiting_approval') : t('creatorJourney.footer.personal_only')}</span>
              {activeStep === 2 && <Button variant="primary" className={baseStyles.nextButton} onClick={openDeliverables}>{t('creatorJourney.actions.open_deliverables')} <ArrowRight size={17} /></Button>}
              {activeStep === 3 && <Button variant="primary" className={baseStyles.nextButton} onClick={() => setActiveStep(4)} disabled={!deliverablesReady}>{t('creatorJourney.actions.continue_review')} <ArrowRight size={17} /></Button>}
              {activeStep === 4 && <Button variant="primary" className={baseStyles.nextButton} onClick={() => setActiveStep(5)} disabled={!appApproved}>{t('creatorJourney.actions.open_earnings')} <ArrowRight size={17} /></Button>}
              {activeStep === 5 && <Button variant="primary" className={baseStyles.nextButton} onClick={() => setActiveStep(6)} disabled={!appApproved}>{t('creatorJourney.actions.view_impact')} <ArrowRight size={17} /></Button>}
              {activeStep === 6 && <Button variant="primary" className={baseStyles.nextButton} onClick={onBack}>{t('creatorJourney.actions.back_to_collabs')} <ArrowRight size={17} /></Button>}
            </div>
          </div>
        </section>
      </div>
    </main>
  );
};

export default CreatorCollabJourney;
